import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, FileUp, RefreshCw, Plus, GitBranch, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, inp, lbl } from "../gear/GearBits";

/* Import legs into an EXISTING trip: paste text or upload a screenshot/PDF —
   AI extracts the legs, every field stays editable, then either add them to this
   trip or spin them off as a stand-alone side trip linked to it. */

const MODE_OPTS = ["flight", "train", "bus", "ferry", "taxi", "car", "hotel", "pickup", "reservation", "other"];
const cell = "border border-zinc-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:border-primary w-full";

export default function SegmentImport({ trip, onAdd, onClose }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [rows, setRows] = useState(null); // extracted, editable segments
  const [sideTitle, setSideTitle] = useState("");

  const runAi = async (files) => {
    const jobs = [];
    if (text.trim()) jobs.push({ text: text.trim() });
    (files || []).slice(0, 6).forEach((f) => jobs.push({ file: f }));
    if (!jobs.length) { toast.error("Paste itinerary text or upload screenshots / a PDF"); return; }
    setBusy(true);
    const found = [];
    try {
      for (let i = 0; i < jobs.length; i++) {
        if (jobs.length > 1) setProgress(`Reading ${i + 1} of ${jobs.length}…`);
        const fd = new FormData();
        if (jobs[i].text) fd.append("text", jobs[i].text);
        if (jobs[i].file) fd.append("file", jobs[i].file);
        try {
          const { data } = await api.post("/trip-import/ai", fd, { timeout: 120000 });
          found.push(...(data.trips || []).flatMap((t) => t.segments || []));
        } catch (err) {
          toast.error(`Couldn't read ${jobs[i].file?.name || "that text"} — ${err?.response?.data?.detail || "try again"}`);
        }
      }
      // combine everything found: dedupe same ref+date+origin across screenshots, sort chronologically
      const seen = new Set();
      const combined = [];
      for (const s of found) {
        const key = `${(s.ref || "").toUpperCase()}|${s.date || ""}|${(s.from_place || "").toLowerCase()}`;
        if (s.ref && seen.has(key)) continue;
        seen.add(key);
        combined.push({ ...s, include: true });
      }
      combined.sort((a, b) => `${a.date || "9999"} ${a.time || "99"}`.localeCompare(`${b.date || "9999"} ${b.time || "99"}`));
      if (!combined.length) { toast.error("No legs found — try adding more detail"); return; }
      setRows(combined);
      toast.success(`${combined.length} leg${combined.length > 1 ? "s" : ""} extracted${jobs.length > 1 ? ` from ${jobs.length} sources` : ""} — review & edit before saving`);
    } finally { setBusy(false); setProgress(""); }
  };

  const set = (i, k) => (e) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const selected = (rows || []).filter((r) => r.include);

  const addToTrip = async () => {
    if (!selected.length) { toast.error("Tick at least one leg"); return; }
    setBusy(true);
    try { await onAdd(selected); onClose(); }
    finally { setBusy(false); }
  };

  const createSideTrip = async () => {
    if (!selected.length) { toast.error("Tick at least one leg"); return; }
    setBusy(true);
    try {
      const dates = selected.flatMap((s) => [s.date, s.arrive_date]).filter(Boolean).sort();
      const stops = [...new Set(selected.flatMap((s) => [s.from_place, s.to_place]).filter(Boolean))].slice(0, 10);
      const title = sideTitle.trim() || `${stops[stops.length - 1] || "Side trip"} — from ${trip.title}`.slice(0, 80);
      const { data } = await api.post("/trips", {
        title, start_date: dates[0] || null, end_date: dates[dates.length - 1] || null,
        stops, parent_trip_id: trip.id,
        description: `Side trip of "${trip.title}".`,
      });
      await api.put(`/trips/${data.id}/segments`, { segments: selected });
      toast.success(`Side trip "${title}" created — linked to ${trip.title}`);
      onClose();
      navigate(`/trips/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create the side trip"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Import legs — ${trip.title}`} onClose={onClose} wide>
      <div data-testid="segment-import-modal">
        {!rows ? (
          <>
            <p className="text-sm text-zinc-700 mb-3">
              Paste a confirmation email / itinerary text — or upload <b>one or several screenshots</b> or a
              PDF of your bookings — and AI extracts the flights, trains, hotels and tours from everything
              at once. You review and edit before it's saved.
            </p>
            <label className={lbl}>Itinerary text</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} maxLength={20000}
              className={inp} placeholder={"e.g.\n6E 512 · DEL Delhi → BOM Mumbai · Oct 2, 9:00 AM\nTaj Palace Mumbai · Oct 2–5"}
              data-testid="segimp-text" />
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button onClick={() => runAi([])} disabled={busy} data-testid="segimp-run"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-violet-700 text-white text-sm font-700 hover:bg-violet-600 disabled:opacity-50">
                {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? (progress || "Extracting…") : "Extract legs"}
              </button>
              <label className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-violet-300 text-violet-700 text-sm font-700 cursor-pointer hover:bg-violet-50">
                <FileUp className="w-4 h-4" /> or upload screenshots / PDF
                <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
                  multiple disabled={busy} data-testid="segimp-file"
                  onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) runAi(fs); }} />
              </label>
              <span className="text-[11px] text-muted-foreground">Up to 6 files at once — results are combined.</span>
            </div>
          </>
        ) : (
          <div data-testid="segimp-preview">
            <p className="text-sm text-zinc-700 mb-3">
              <b>{rows.length} leg{rows.length > 1 ? "s" : ""} extracted</b> — every field is editable.
              Untick anything you don't want, then add them to this trip or create a linked side trip.
            </p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
              {rows.map((r, i) => (
                <div key={i} className={`border p-2 ${r.include ? "border-emerald-300 bg-emerald-50/30" : "border-zinc-200 opacity-60"}`}
                  data-testid={`segimp-row-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <input type="checkbox" checked={r.include} data-testid={`segimp-check-${i}`}
                      onChange={() => setRows((arr) => arr.map((x, j) => (j === i ? { ...x, include: !x.include } : x)))} />
                    <select value={r.mode} onChange={set(i, "mode")} className="border border-zinc-300 bg-white px-1.5 py-1.5 text-xs font-700 capitalize" data-testid={`segimp-mode-${i}`}>
                      {MODE_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <input value={r.ref || ""} onChange={set(i, "ref")} placeholder={r.mode === "hotel" ? "Res #" : "Ref #"} className={`${cell} !w-24`} data-testid={`segimp-ref-${i}`} />
                    <input value={r.carrier || ""} onChange={set(i, "carrier")} placeholder={r.mode === "hotel" ? "Provider" : "Carrier"} className={`${cell} flex-1`} />
                    {r.mode === "hotel" && (
                      <span className="shrink-0 px-1.5 py-0.5 bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-700"
                        title="Saved into this trip's Stays as a confirmed reservation" data-testid={`segimp-stay-chip-${i}`}>
                        → Stays · {r.stay_status || "confirmed"}
                      </span>
                    )}
                    <button onClick={() => setRows((arr) => arr.filter((_, j) => j !== i))}
                      className="text-zinc-300 hover:text-rose-600 shrink-0" data-testid={`segimp-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    <input value={r.from_place || ""} onChange={set(i, "from_place")} placeholder={r.mode === "hotel" ? "Property" : "From"} className={cell} data-testid={`segimp-from-${i}`} />
                    <input value={r.to_place || ""} onChange={set(i, "to_place")} placeholder={r.mode === "hotel" ? "City" : "To"} className={cell} data-testid={`segimp-to-${i}`} />
                    <input type="date" value={r.date || ""} onChange={set(i, "date")} className={cell} data-testid={`segimp-date-${i}`} />
                    <input type="time" value={r.time || ""} onChange={set(i, "time")} className={cell} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t border-zinc-200 pt-3 space-y-3">
              <button onClick={addToTrip} disabled={busy || !selected.length} data-testid="segimp-add-to-trip"
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-primary text-white text-sm font-700 hover:bg-blue-700 disabled:opacity-50">
                <Plus className="w-4 h-4" /> Add {selected.length} leg{selected.length !== 1 ? "s" : ""} to this trip
              </button>
              <div className="flex gap-2">
                <input value={sideTitle} onChange={(e) => setSideTitle(e.target.value)} maxLength={80}
                  placeholder="Side trip title (e.g. Goa hop — Oct 2026)" className={`${inp} flex-1`} data-testid="segimp-side-title" />
                <button onClick={createSideTrip} disabled={busy || !selected.length} data-testid="segimp-create-side"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-violet-500 text-violet-700 text-sm font-700 hover:bg-violet-50 disabled:opacity-50 shrink-0">
                  <GitBranch className="w-4 h-4" /> Create linked side trip
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                A <b>side trip</b> is its own trip (own timeline, packing &amp; expenses) that stays linked
                to “{trip.title}” — perfect for domestic hops inside a bigger journey.
              </p>
              <button onClick={() => setRows(null)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-zinc-800" data-testid="segimp-back">
                <ArrowLeft className="w-3.5 h-3.5" /> Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
